// public/v3/src/components/bhi-assessment-modal.jsx
// Modal for recording a BHI rating-scale assessment (PHQ-9, GAD-7, etc.).
//
// Used from Time Logs (BHI view). Touch-first: score field auto-focused,
// numeric keypad on iOS, native date picker, progressive disclosure for
// date + time-spent under an "adjust" link.
//
// Save writes both a structured assessment record (data.bhi.assessments[])
// and an activity-log entry tagged with the patient's billing.bhiProgram
// CPT — see lib/bhi-assessment-entry.js for the exact shapes.
//
// Unsaved-changes guard via lib/hooks/use-unsaved-changes-guard.js: any
// dismiss path (backdrop tap, Cancel, X close, Esc) on a dirty form prompts
// the user to confirm discard.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ccmApi } from '../lib/api';
import { resolveEffectiveScales, lookupScale, computeSeverity } from '../lib/bhi-scales';
import { suggestBhi } from '../lib/bhi-suggest';
import { buildAssessmentEntry, severityCssToken } from '../lib/bhi-assessment-entry';
import { useUnsavedChangesGuard } from '../lib/hooks/use-unsaved-changes-guard';
import { _latestAssessmentForMonth } from '../lib/bhi-billing';
import { formatLastRecorded } from '../lib/bhi-last-recorded';

function todayYmd() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

export function BhiAssessmentModal({ patient, settings, currentUser, onClose, onSaved }) {
  // Capture raw once so every hook reads from a single stable expression. When
  // patient is missing, raw is null and the hooks fall back to safe defaults;
  // the render-time early return below bails out before any patient-bearing
  // JSX runs. Hooks must be called unconditionally in the same order on every
  // render (Rules of Hooks), so the early return cannot precede them.
  const raw = patient?._raw || null;

  const effectiveScales = useMemo(() => resolveEffectiveScales(settings || {}), [settings]);
  const previousAssessments = raw?.bhi?.assessments || [];
  const lastAssessment = previousAssessments[previousAssessments.length - 1] || null;
  const suggested = useMemo(
    () => raw ? suggestBhi(raw, settings || {}) : { suggestedScale: '' },
    [raw, settings]
  );

  const defaultScale = (lastAssessment && lastAssessment.scale)
    || suggested.suggestedScale
    || (effectiveScales[0] && effectiveScales[0].id)
    || 'PHQ-9';

  const [scale, setScale] = useState(defaultScale);
  const [date, setDate] = useState(todayYmd());
  const [score, setScore] = useState('');
  const [notes, setNotes] = useState('');
  const [timeSpent, setTimeSpent] = useState(5);
  const [showAdjust, setShowAdjust] = useState(false);
  const [scaleOpen, setScaleOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const scoreRef = useRef(null);

  // Auto-focus the score field on open. Done in a useEffect so the input
  // exists in the DOM before focus() is called.
  useEffect(() => {
    if (scoreRef.current) scoreRef.current.focus();
  }, []);

  // Recompute severity live from score + scale.
  const scaleDef = lookupScale(scale, settings || {});
  const maxScore = scaleDef ? scaleDef.maxScore : 100;
  const numericScore = Number(score);
  const scoreValid = score !== '' && Number.isFinite(numericScore) && numericScore >= 0 && numericScore <= maxScore;
  const severity = scoreValid ? computeSeverity(scale, numericScore, settings || {}) : '';

  // Dirty-state guard
  const { attemptDismiss, showDiscardPrompt, confirmDiscard, keepEditing } = useUnsavedChangesGuard({
    values: { scale, date, score, notes, timeSpent },
    onClose,
    busy,
  });

  // Esc key dismissal
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') attemptDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attemptDismiss]);

  // Bail out AFTER hooks (Rules of Hooks). All hook calls above tolerate a
  // null raw via the optional-chaining and the ternary in suggested.
  if (!patient || !raw) return null;

  async function handleSave(e) {
    if (e) e.preventDefault();
    if (!scoreValid) {
      setError('Score must be a number between 0 and ' + maxScore + '.');
      return;
    }
    // A future-dated assessment would satisfy the month gate for a service
    // not yet rendered — the note must never assert one.
    if (!date || date > todayYmd()) {
      setError('Assessment date must be on or before today.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const form = { scale, date, score: numericScore, severity, notes, timeSpent: Number(timeSpent) || 5 };
      const { assessmentRecord, activityLogEntry } = buildAssessmentEntry(patient._raw, form, currentUser);
      const existingAssessments = Array.isArray(patient._raw?.bhi?.assessments) ? patient._raw.bhi.assessments : [];
      const existingLog = Array.isArray(patient._raw?.activityLog) ? patient._raw.activityLog : [];
      const nextData = {
        ...patient._raw,
        bhi: { ...(patient._raw.bhi || {}), assessments: [...existingAssessments, assessmentRecord] },
        activityLog: [...existingLog, activityLogEntry],
      };
      const resp = await ccmApi.updatePatient(patient.id, nextData);
      const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
      const updated = fresh ? { ...nextData, _updatedAt: fresh } : nextData;
      onSaved && onSaved(updated, { assessmentRecord, activityLogEntry });
      onClose && onClose();
    } catch (err) {
      if (err && err.status === 409) {
        setError('Patient was updated elsewhere. Refresh and try again.');
      } else {
        setError(err && err.message ? err.message : 'Save failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  const patientName = [patient._raw?.fname, patient._raw?.lname].filter(Boolean).join(' ') || patient.name || patient.id;
  const monthLabel = date.slice(0, 7);

  // Carried-forward assessment that counts for the month being recorded into
  // (latest dated on/before month-end, any scale). Read-only context — billing
  // does not require a fresh score each month.
  const onFileAssessment = _latestAssessmentForMonth(raw.bhi && raw.bhi.assessments, monthLabel);
  const lastRecordedLine = formatLastRecorded(onFileAssessment);

  return (
    <div className="modal-backdrop" onClick={attemptDismiss}>
      <div
        className="modal bhi-assessment-modal"
        role="dialog"
        aria-modal="true"
        aria-label={showDiscardPrompt ? 'Discard assessment' : `Record assessment for ${patientName}`}
        onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>{showDiscardPrompt ? 'Discard assessment?' : 'Record assessment'}</h3>
            {!showDiscardPrompt && (
              <div className="sub">{patientName} · {monthLabel}</div>
            )}
          </div>
          <button className="btn ghost icon" onClick={attemptDismiss} aria-label="Close">✕</button>
        </div>

        {showDiscardPrompt ? (
          <>
            <div className="modal-body">
              <p>You've entered a score but haven't saved yet.</p>
            </div>
            <div className="modal-foot">
              <button type="button" className="btn" onClick={keepEditing}>Keep editing</button>
              <button type="button" className="btn warn" onClick={confirmDiscard}>Discard</button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSave}>
            <div className="modal-body">
              <div className="bhi-last-recorded">{lastRecordedLine || 'No prior assessment on file.'}</div>
              <div className="field">
                <div className="bhi-scale-row">
                  <span className="bhi-scale-name">{scaleDef ? scaleDef.name : scale}</span>
                  <button type="button" className="btn ghost sm pill" onClick={() => setScaleOpen((v) => !v)}>
                    change ▾
                  </button>
                </div>
                {scaleOpen && (
                  <select
                    className="bhi-scale-picker"
                    value={scale}
                    onChange={(e) => { setScale(e.target.value); setScaleOpen(false); }}>
                    {effectiveScales.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="field">
                <div className="field-label">Score (0–{maxScore})</div>
                <div className="bhi-score-row">
                  <input
                    ref={scoreRef}
                    type="number"
                    className="score-input tabnum"
                    inputMode="numeric"
                    min={0}
                    max={maxScore}
                    value={score}
                    onChange={(e) => setScore(e.target.value)}
                    disabled={busy} />
                  {severity && (
                    <span className={'severity-badge ' + severityCssToken(severity)}>{severity}</span>
                  )}
                </div>
              </div>

              <div className="field">
                <div className="field-label">Note (optional)</div>
                <textarea
                  className="bhi-notes-input"
                  rows={2}
                  maxLength={240}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={busy}
                  autoCapitalize="sentences"
                  autoCorrect="on" />
              </div>

              <div className="bhi-adjust-caption">
                <span>
                  Logs {timeSpent} min of BHI time on {date}
                </span>
                <button type="button" className="btn link sm" onClick={() => setShowAdjust((v) => !v)}>
                  {showAdjust ? 'hide' : 'adjust'}
                </button>
              </div>

              {showAdjust && (
                <div className="bhi-adjust-fields">
                  <div className="field">
                    <div className="field-label">Date</div>
                    <input type="date" value={date} max={todayYmd()} onChange={(e) => setDate(e.target.value)} disabled={busy} />
                  </div>
                  <div className="field">
                    <div className="field-label">Time spent (min)</div>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={60}
                      value={timeSpent}
                      onChange={(e) => setTimeSpent(e.target.value)}
                      disabled={busy} />
                  </div>
                </div>
              )}

              {error && <div className="bhi-modal-error">{error}</div>}
            </div>

            <div className="modal-foot">
              <button type="button" className="btn" onClick={attemptDismiss} disabled={busy}>Cancel</button>
              <button type="submit" className="btn primary" disabled={busy || !scoreValid}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
